import {
    Alert,
    AlertDescription,
    ToggleGroup,
    ToggleGroupItem,
    Tooltip,
    TooltipTrigger,
    TooltipContent,
    Button,
    useToast,
} from '@quadminds/mindkit';
import { ThumbsDown, ThumbsUp, Copy, Check } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import { useCopyToClipboard } from 'usehooks-ts';

export interface MessageActionsProps {
    /** Current rating value (null, 'up', or 'down') */
    rating: string | null;
    /** Whether the rating request is pending */
    isPending: boolean;
    /** Whether to show the feedback input */
    showFeedbackInput: boolean;
    /** Current status of the feedback submission */
    status: 'idle' | 'success' | 'error';
    /** Text content to copy to clipboard */
    textToCopy: string;
    /** Callback when rating is toggled */
    onRatingToggle: (value: 'up' | 'down') => void;
    /** Whether rating buttons should be shown (requires interactionId to be available) */
    canRate?: boolean;
    /** Additional CSS classes */
    className?: string;
}

export const MessageActions: React.FC<MessageActionsProps> = ({
    rating,
    isPending,
    showFeedbackInput,
    status,
    textToCopy,
    onRatingToggle,
    canRate = false,
    className,
}) => {
    const { t } = useTranslation();
    const { toast } = useToast();
    const [, copyToClipboard] = useCopyToClipboard();
    const [justCopied, setJustCopied] = React.useState(false);

    const formatTextForClipboard = (text: string): string => {
        let result = text;

        // Remove fenced code blocks (keep content inside) using split to avoid ReDoS
        const codeParts = result.split('```');
        result = codeParts
            .map((part, index) => {
                if (index % 2 === 1) {
                    // Inside a code block: remove the language identifier on the first line
                    const newlineIndex = part.indexOf('\n');
                    // If there's no newline (e.g. unmatched or inline fence), keep the content as-is
                    return newlineIndex !== -1 ? part.substring(newlineIndex + 1) : part;
                }
                return part;
            })
            .join('');

        result = result.replaceAll(/`([^`]*)`/g, '$1');

        result = result.replaceAll(/^#{1,6}[ \t]+/gm, '');

        result = result.replaceAll(/\*\*([^*\n]+)\*\*/g, '$1');
        result = result.replaceAll(/__([^_\n]+)__/g, '$1');

        result = result.replaceAll(/\*([^*\n]+)\*/g, '$1');
        result = result.replaceAll(/_([^_\n]+)_/g, '$1');
        {
            let parsed = '';
            let i = 0;
            while (i < result.length) {
                // Check for image ![alt](url)
                if (result[i] === '!' && i + 1 < result.length && result[i + 1] === '[') {
                    const closeBracket = result.indexOf(']', i + 2);
                    if (closeBracket !== -1 && result[closeBracket + 1] === '(') {
                        const closeParen = result.indexOf(')', closeBracket + 2);
                        if (closeParen !== -1) {
                            parsed += result.substring(i + 2, closeBracket);
                            i = closeParen + 1;
                            continue;
                        }
                    }
                }
                if (result[i] === '[') {
                    const closeBracket = result.indexOf(']', i + 1);
                    if (closeBracket !== -1 && result[closeBracket + 1] === '(') {
                        const closeParen = result.indexOf(')', closeBracket + 2);
                        if (closeParen !== -1) {
                            parsed += result.substring(i + 1, closeBracket);
                            i = closeParen + 1;
                            continue;
                        }
                    }
                }
                parsed += result[i];
                i++;
            }
            result = parsed;
        }

        result = result.replaceAll(/^>[ \t]+/gm, '');

        result = result.replaceAll(/^[-*_]{3,}$/gm, '');
        result = result.replaceAll(/^([ \t]*)[-*+][ \t]+/gm, '$1• ');
        result = result.replaceAll(/\n{3,}/g, '\n\n');

        return result.trim();
    };

    const handleCopyToClipboard = async () => {
        try {
            const copied = await copyToClipboard(formatTextForClipboard(textToCopy));
            if (!copied) throw new Error('Copy failed');
            setJustCopied(true);
            toast({
                title: t('chat.response.copied'),
                description: t('chat.response.copiedDescription'),
                variant: 'default',
                duration: 3000,
            });

            // Reset the copied state after 2 seconds
            setTimeout(() => setJustCopied(false), 2000);
        } catch {
            toast({
                title: t('chat.response.copyError'),
                description: t('chat.response.copyErrorDescription'),
                variant: 'destructive',
                duration: 5000,
            });
        }
    };

    return (
        <div
            data-testid="response-actions"
            className={twMerge(
                `flex flex-col space-y-2 items-start w-full transition opacity-0 group-hover:opacity-100`,
                showFeedbackInput && 'opacity-100',
                className,
            )}
        >
            <div className="flex items-center gap-2">
                {/* Rating Toggle Group — only shown once interactionId is available */}
                {canRate && (
                    <ToggleGroup type="single" size="sm" value={rating ?? undefined} className="gap-2">
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <ToggleGroupItem
                                    value="up"
                                    aria-label="Rate positive"
                                    onClick={() => onRatingToggle('up')}
                                    disabled={isPending}
                                >
                                    <ThumbsUp className="w-4 h-4" />
                                </ToggleGroupItem>
                            </TooltipTrigger>
                            <TooltipContent>
                                <span className="text-xs">{t('chat.response.ok')}</span>
                            </TooltipContent>
                        </Tooltip>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <ToggleGroupItem
                                    value="down"
                                    aria-label="Rate negative"
                                    onClick={() => onRatingToggle('down')}
                                    disabled={isPending}
                                >
                                    <ThumbsDown className="w-4 h-4" />
                                </ToggleGroupItem>
                            </TooltipTrigger>
                            <TooltipContent>
                                <span className="text-xs">{t('chat.response.notOk')}</span>
                            </TooltipContent>
                        </Tooltip>
                    </ToggleGroup>
                )}

                {/* Copy to Clipboard Button */}
                <Tooltip>
                    <TooltipTrigger asChild>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleCopyToClipboard}
                            disabled={!textToCopy}
                            className="h-9 w-9 p-0"
                            aria-label="Copy to clipboard"
                        >
                            {justCopied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                        </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                        <span className="text-xs">
                            {justCopied ? t('chat.response.copied') : t('chat.response.copyToClipboard')}
                        </span>
                    </TooltipContent>
                </Tooltip>

                {/* Status Alerts */}
                {status === 'success' && (
                    <Alert variant="success" className="px-3 py-2">
                        <AlertDescription className="text-xs">{t('chat.response.successRequest')}</AlertDescription>
                    </Alert>
                )}
                {status === 'error' && (
                    <Alert variant="error" className="px-3 py-2">
                        <AlertDescription className="text-xs">{t('chat.response.errorRequest')}</AlertDescription>
                    </Alert>
                )}
            </div>
        </div>
    );
};

export default MessageActions;
